import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { Anexo, catchRenderException, Endpoint, IeAnexo, shouldRenderMobile, ToastServiceStipulator } from '@philips-emr/hpms-stipulator-commons';
import { HPMSOnOpen, HPMSOnRenderAction } from '@philips-emr/hpms-stipulator-components/lib/events/event';
import { HpmsCustomContentCollapseEvent } from '@philips-emr/hpms-stipulator-components/lib/types/menu-collapse-card-type';
import { Button, FieldListAPI, PanelAPI, Stamp, StampIcon } from '@philips-emr/tws-components';
import { ButtonTheme, Color, Placement } from '@philips-emr/tws-constants';
import Controller, { OnAfterFetch, OnDynamicContentRender, OnMount, OnOpen, OnSelect } from '@philips-emr/tws-core/lib/client';
import { FetchEvent, RenderEvent, SelectEvent } from '@philips-emr/tws-core/lib/component/types';
import { FileService, HttpService, MessageService, ModalService } from '@philips-emr/tws-core/lib/services';
import { HttpMethod } from '@philips-emr/tws-core/lib/services/http-service';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { AutorizacaoDadosGuiaAliases as Aliases, FragmentNames } from '../../codegen';
import { TipoOrigemAnexo } from '../anexo-tiss/anexo-tiss-controller';
import './autorizacao-dados-guia-controller.css';
import BaseController from '../BaseController';

enum IeTipoItem {
  P = 'P',
  M = 'M',
}

enum IeStatus {
  A = 'a',
  C = 'c',
  D = 'd',
  I = 'i',
  K = 'k',
  L = 'l',
  M = 'm',
  N = 'n',
  P = 'p',
  S = 's',
  U = 'u',
}

interface GuiaItem {
  nrSequencia: number;
  tipoItem: IeTipoItem;
  dsTipo: string;
  ieStatus: IeStatus;
  ieTipoAnexo: string;
}

interface GlosaOcorrencia {
  descricao: string;
}

export interface AnexoTiss {
  dsMaterial: string;
}

function getStampExclusaoBeneficiario(ieStatus: IeStatus): ReactElement | null {
  const label = `@historico-autorizacao.status-prod-mat/${ieStatus}`;
  let stampProps = null;

  switch (ieStatus) {
    case IeStatus.A:
      stampProps = { color: Color.SAFETY_ORANGE, icon: StampIcon.WARNING, label };
      break;
    case IeStatus.C:
      stampProps = { color: Color.SAFETY_BLUE, icon: StampIcon.CHECK, label };
      break;
    case IeStatus.D:
      stampProps = { color: Color.SAFETY_NEUTRAL, icon: StampIcon.CANCELED_OUTLINE, label };
      break;
    case IeStatus.I:
      stampProps = { className: 'c-autorizada-dado-guia-stamp__stamp--color', icon: StampIcon.CANCELED_OUTLINE, label };
      break;
    case IeStatus.K:
      stampProps = { color: Color.SAFETY_RED, icon: StampIcon.CANCELED, label };
      break;
    case IeStatus.L:
      stampProps = { color: Color.SAFETY_GREEN, icon: StampIcon.CHECK, label };
      break;
    case IeStatus.M:
      stampProps = { color: Color.SAFETY_RED, icon: StampIcon.CANCELED, label };
      break;
    case IeStatus.N:
      stampProps = { color: Color.SAFETY_RED, icon: StampIcon.CANCELED, label };
      break;
    case IeStatus.P:
      stampProps = { color: Color.SAFETY_GREEN, icon: StampIcon.CHECK, label };
      break;
    case IeStatus.S:
      stampProps = { color: Color.SAFETY_GREEN, icon: StampIcon.CHECK, label };
      break;
    case IeStatus.U:
      stampProps = { color: Color.SAFETY_NEUTRAL, icon: StampIcon.CANCELED_OUTLINE, label };
      break;
    default:
      return null;
  }

  return <Stamp {...stampProps} />;
}

@Controller({ fragment: FragmentNames.AUTORIZACAO_DADOS_GUIA })
export default class AutorizacaoDadosGuiaController extends BaseController {

  @Inject(Aliases.FIELD_LIST_GLOSAS_OCORRENCIAS)
  private fieldListGlosasOcorrencias: FieldListAPI;

  @Inject(Aliases.PANEL_GLOSAS_OCORRENCIAS)
  private panelGlosasOcorrencias: PanelAPI;

  @Inject(Aliases.PANEL_PROCEDIMENTO_MATERIAL)
  private panelProcedimentoMaterial: PanelAPI;

  @Inject(Aliases.PANEL_ANEXO)
  private panelAnexo: PanelAPI;

  private glosasOcorrencias?: GlosaOcorrencia[];

  @OnMount(Aliases.LAYOUT_AUTORIZACAO_DADOS_GUIA)
  public onMountLayoutAutorizacaoDadosGuia(): void {
    this.initProps();

    const { nrSeqContrato, nrSeqGuia } = this.historyService.getCurrentRouteParams();
    this.fetchAutorizacaoGlosaOcorrencia(nrSeqContrato, nrSeqGuia);
  }

  @OnOpen(Aliases.ACCORDION_GLOSAS_OCORRENCIA)
  public onOpenAccordionGlosasOcorrencia(): void {
    catchRenderException(() => this.fieldListGlosasOcorrencias.setValue(this.glosasOcorrencias as any));
  }

  @OnAfterFetch(Aliases.FETCH_PROCEDIMENTO_MATERIAL)
  public onAfterFetchProcedimentoMaterial(event: FetchEvent): void {
    if (!event.data) {
      return;
    }
    const data = event.data as [];

    if (isEmptyArrayOrObject(data)) {
      this.panelProcedimentoMaterial.setVisible(false);
    }
  }

  @OnAfterFetch(Aliases.FETCH_ANEXO)
  public onAfterFetchAnexo(event: FetchEvent): void {
    if (!event.data) {
      return;
    }
    const data = event.data as [];

    if (isEmptyArrayOrObject(data)) {
      this.panelAnexo.setVisible(false);
    }

  }

  private initProps(): void {
    this.glosasOcorrencias = undefined;
  }

  @OnSelect(Aliases.ATTACHMENTS_LIST_ANEXO)
  public onSelectAttachment(event: SelectEvent<Anexo>): void {
    const { value } = event;
    const { filename, nrSeqAnexo } = value;
    const { nrSeqContrato } = this.historyService.getCurrentRouteParams();
    const ieTabela = IeAnexo.GUIA;

    FileService.download(Endpoint.GET.GUIA_DOWNLOAD, filename, { ieTabela, nrSeqAnexo, nrSeqContrato }, HttpMethod.GET)
      .catch(() => ToastServiceStipulator.error(MessageService.translate('stipulator.arquivo-nao-localizado')));
  }

  @OnDynamicContentRender(Aliases.HPMS_DYNAMIC_STAMP)
  public onDynamicContentRender(event: RenderEvent): void {
    const { data, render } = event;
    const { ieStatus } = data as GuiaItem;
    const stampExclusao = getStampExclusaoBeneficiario(ieStatus);

    if (stampExclusao) {
      render((): ReactElement => stampExclusao);
    }
  }

  @HPMSOnRenderAction(Aliases.HPMS_CUSTOM_CARD_PROCEDIMENTO_MATERIAL)
  public onRenderAction(event: RenderEvent): void {
    const { render, data } = event;
    const { nrSequencia, ieTipoAnexo } = data as GuiaItem;

    if (!shouldRenderMobile() || !['OP', 'QU'].includes(ieTipoAnexo)) {
      return;
    }

    render(() => (
      <Button
        key="report"
        icon="/static/assets/view-details.svg"
        theme={ButtonTheme.QUIET_EMPHASIS}
        value=""
        iconPlacement={Placement.RIGHT}
        onClick={() => this.openModalMaterial(nrSequencia, ieTipoAnexo)}
      />
    ));
  }

  @OnDynamicContentRender(Aliases.HPMS_DYNAMIC_ACTION)
  public onDynamicContentRenderAction(event: RenderEvent): void {
    const { render, data } = event;
    const { nrSequencia, ieTipoAnexo } = data as GuiaItem;

    if (shouldRenderMobile() || !['OP', 'QU'].includes(ieTipoAnexo)) {
      return;
    }

    render(() => (
      <Button
        key="report"
        icon="/static/assets/view-details.svg"
        theme={ButtonTheme.QUIET_EMPHASIS}
        value=""
        iconPlacement={Placement.RIGHT}
        onClick={() => this.openModalMaterial(nrSequencia, ieTipoAnexo)}
        className="c-autorizada-dado-guia-item_item-button-action"
      />
    ));
  }

  private openModalMaterial(nrSeqMat: number, ieTipoAnexo: string): void {
    const nrSequencia = this.context.get('nrSeqGuia');
    const nrSeqContrato = this.context.get('nrSeqContrato');

    ModalService.openDialog({
      title: 'historico-autorizacao.detalhes-item',
      fragment: FragmentNames.ANEXO_ITEM_PROD_MAT,
      contextData: {
        nrSeqContrato,
        nrSequencia,
        nrSeqMat,
        ieTipoAnexo,
        origem: TipoOrigemAnexo.ANEXOS_TISS_AUTORIZACOES,
      },
    });
  }

  private fetchAutorizacaoGlosaOcorrencia(nrSeqContrato: number | string, nrSeqGuia: number | string): void {
    const setContext = (): void => this.context.set({ nrSeqContrato, nrSeqGuia });

    void HttpService.get<GlosaOcorrencia[]>(Endpoint.GET.AUTORIZACAO_GLOSAS_OCORRENCIAS, { nrSeqContrato, nrSeqGuia })
      .then(({ body: glosasOcorrencias }) => {
        if (isEmptyArrayOrObject(glosasOcorrencias)) {
          setContext();
          this.panelGlosasOcorrencias.setVisible(false);
          return;
        }

        this.glosasOcorrencias = glosasOcorrencias;
        catchRenderException(() => this.fieldListGlosasOcorrencias.setValue(glosasOcorrencias as any));

        setContext();
        this.context.set({ nrSeqContrato, nrSeqGuia });
      }).catch(() => setContext());
  }

  @HPMSOnOpen(Aliases.HPMS_CUSTOM_CARD_CONTENT_COLLAPSE)
  public onOpenRenderContent(event: HpmsCustomContentCollapseEvent<GuiaItem>): void {
    if (!event || !event.data) {
      return;
    }

    const { data: guiaItem, onOpenRender } = event;
    let endpoint;

    if (guiaItem && guiaItem.tipoItem === IeTipoItem.M) {
      endpoint = Endpoint.GET.AUTORIZACAO_MATERIAL_GLOSAS_OCORRENCIAS;
    } else {
      endpoint = Endpoint.GET.AUTORIZACAO_PROCEDIMENTO_GLOSAS_OCORRENCIAS;
    }

    onOpenRender({ endpoint, parameters: { nrSeqItem: guiaItem.nrSequencia } });
  }

}
